import merge from 'lodash/merge';
import uniqBy from 'lodash/uniqBy';
import type { Location, PathRef } from 'slate';
import { Editor, Node, Path, Transforms } from 'slate';
import type { DeepPartial } from 'ts-essentials';
import { v4 as uuid4 } from 'uuid';

import type { UUID } from '../../types/type-helpers';
import type {
  AlineTextFormats,
  EditingMode,
  ListDefinition,
  ListDefinitions,
  ListItemElement,
  ListMarker,
  ListPaths,
  ParagraphElement,
  ParagraphProps,
} from '../internal';
import {
  AlineTransforms,
  createDefaultListItemIndentation,
  createListDefinition,
  getListMarkerText,
  HistoryTransforms,
  isListItem,
  isListLevel,
  isParagraphElement,
  Marks,
  Queries,
  SelEventSource,
} from '../internal';

interface CreateListDefinition {
  listDefinition: ListDefinition;
  listDefinitionId: UUID;
}

interface BuildListMarkerOptions {
  listDefinitions: ListDefinitions;
  listItem: ListItemElement;
  /**
   * The list path indicating its numbering as a path.
   * For example, [0, 0, 0] may result in marker text 1.1.1.
   */
  listItemPath: Path;
}
// *Content model* path as a string to ListMarker.
export type PathToListMarker = Record<string, ListMarker>;

export const ListTransforms = {
  /**
   * Builds the ListMarker object if the relevant paragraph is a list item.
   */
  buildListMarker(
    editor: Editor,
    { listDefinitions, listItem, listItemPath }: BuildListMarkerOptions
  ): ListMarker | null {
    const details = Queries.activeListMarkerDetails(listDefinitions, listItem);
    if (!details) return null;

    const { activeListProps, levelDefinition } = details;
    const activeStyleSheet = Queries.styleSheet(editor);
    const activeVariant = Queries.renderedParagraphStyleVariant(listItem);
    const text = getListMarkerText(levelDefinition, {
      listLevel: activeListProps.listLevel,
      listPath: listItemPath,
    });
    const { styles: styleSheetTextFormats } =
      activeVariant in activeStyleSheet
        ? activeStyleSheet[activeVariant]
        : { styles: Marks.default };

    return {
      ...styleSheetTextFormats,
      ...listItem.glyphProps,
      ...levelDefinition.markerFormats,
      comments: [],
      listLevelDefinition: levelDefinition,
      suggestedEdits: [],
      text,
    };
  },

  /**
   * Continue the numbering from a previous list.
   *
   * Takes a list and makes it have the same listId as the closest list item
   * that is above from the current one. If there is no list item above
   * anywhere, this does nothing.
   */
  continueList(
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode } = options;
      if (!at) return;

      const startPoint = Editor.start(editor, at);
      const listItemEntries = Editor.nodes<ListItemElement>(editor, {
        at,
        match: isListItem,
      });
      const firstListItemEntry = listItemEntries.next().value;
      if (!firstListItemEntry) return;

      const [firstListItem] = firstListItemEntry;
      const listId = Queries.renderedParagraphListProps(firstListItem)?.listId;
      if (!listId) return;

      const nearestPreviousListEntry = Editor.previous<ListItemElement>(editor, {
        at: startPoint,
        match: node => node !== firstListItem && isListItem(node),
      });
      if (!nearestPreviousListEntry) return;

      const [previousListItem] = nearestPreviousListEntry;
      const previousListId = Queries.renderedParagraphListProps(previousListItem)?.listId;
      if (!previousListId || previousListId === listId) return;

      const range = Editor.range(editor, startPoint, Editor.end(editor, []));
      const listItemsEntriesToChange = [
        ...Editor.nodes<ListItemElement>(editor, {
          at: range,
          match: node => {
            if (!isParagraphElement(node)) return false;

            const activeListProps = Queries.renderedParagraphListProps(node);
            return activeListProps?.listId === listId;
          },
        }),
      ];

      for (const [listItem, listItemPath] of listItemsEntriesToChange) {
        const activeListProps = Queries.renderedParagraphListProps(listItem);
        if (!activeListProps) return;

        AlineTransforms.setParagraphProps(
          editor,
          editingMode,
          authorId,
          {
            listProps: {
              listId: previousListId,
              listLevel: activeListProps.listLevel,
            },
          },
          { at: listItemPath }
        );
      }
    });
  },

  /**
   * Creates a list definition which describes how a list should be formatted.
   */
  createListDefinition: (
    editor: Editor,
    options: {
      listDefinition?: ListDefinition;
      listDefinitionId?: UUID;
      markerFormats?: AlineTextFormats;
    }
  ): CreateListDefinition => {
    return AlineTransforms.withoutNormalizing<CreateListDefinition>(editor, () => {
      const { markerFormats, listDefinition = createListDefinition({ markerFormats }) } = options;
      const [document, documentPath] = Queries.getDocument(editor);
      const listDefinitionId = options.listDefinitionId ?? uuid4();
      const listDefinitionWithFormats: ListDefinition = markerFormats
        ? merge({}, listDefinition, {
            0: { markerFormats },
            1: { markerFormats },
            2: { markerFormats },
            3: { markerFormats },
            4: { markerFormats },
            5: { markerFormats },
            6: { markerFormats },
            7: { markerFormats },
            8: { markerFormats },
          })
        : listDefinition;
      const newListDefinitions: ListDefinitions = {
        ...document.listDefinitions,
        [listDefinitionId]: listDefinitionWithFormats,
      };

      Transforms.setNodes(editor, { listDefinitions: newListDefinitions }, { at: documentPath });

      return {
        listDefinition,
        listDefinitionId,
      };
    });
  },

  /**
   * Creates a record of *content model* paragraph path to the list marker for that path.  The map can be used to fetch
   * the list marker so we know how to render it correctly.
   * Note: Don't wrap this in Editor.withoutNormalizing it causes an infinite loop on the client.
   */
  createPathToListMarker(editor: Editor): PathToListMarker {
    const [document, documentPath] = Queries.getDocument(editor);
    const documentListItemEntries = Editor.nodes(editor, {
      at: documentPath,
      match: isListItem,
      voids: true,
    });
    const headersEntry = Queries.getHeadersElement(editor);
    const footersEntry = Queries.getFootersElement(editor);
    if (!headersEntry || !footersEntry) return {};

    const headerListItemEntries = Queries.activeHeaderTypes(editor)
      .map(type => Queries.getHeaderOrFooter({ editor, partEntry: headersEntry, type }))
      .flatMap(([, path]) => [
        ...Editor.nodes(editor, { at: path, match: isListItem, voids: true }),
      ]);
    const footerListItemEntries = Queries.activeFooterTypes(editor)
      .map(type => Queries.getHeaderOrFooter({ editor, partEntry: footersEntry, type }))
      .flatMap(([, path]) => [
        ...Editor.nodes(editor, { at: path, match: isListItem, voids: true }),
      ]);
    const { listDefinitions } = Queries.getNumbering(editor);
    const { pathToListMarker } = [
      ...documentListItemEntries,
      ...headerListItemEntries,
      ...footerListItemEntries,
    ].reduce<{
      listPaths: ListPaths;
      pathToListMarker: PathToListMarker;
    }>(
      (acc, [listItem, listItemPath]) => {
        const activeListProps = Queries.renderedParagraphListProps(listItem);
        if (!activeListProps) return acc;

        const { listPaths } = acc;
        const { listItemListPath, newListPaths } = AlineTransforms.upsertListPaths(
          document.listDefinitions,
          listPaths,
          activeListProps
        );
        const listMarker = AlineTransforms.buildListMarker(editor, {
          listDefinitions,
          listItem,
          listItemPath: listItemListPath,
        });
        if (!listMarker) return acc;

        acc.pathToListMarker[JSON.stringify(listItemPath)] = listMarker;
        acc.listPaths = newListPaths;

        return acc;
      },
      { listPaths: {}, pathToListMarker: {} }
    );

    return pathToListMarker;
  },

  /**
   * findOrCreate a list definition for toggling list items.  If there is a list definition in the paragraphs
   * in the selection, it returns the first list definition *if it's equal to listDefinition*.  Otherwise,
   * create a new listDefinition. Continuation into a previous matching list is handled separately by
   * toggleListItems so it can be split across undo history.
   */
  findOrCreateToggleListDefinition: (
    editor: Editor,
    options: {
      listDefinition: ListDefinition;
    }
  ): CreateListDefinition & { created: boolean } => {
    return AlineTransforms.withoutNormalizing(editor, () => {
      const { listDefinition } = options;
      const paragraphEntries = [...Queries.paragraphsAtLocation(editor, { listMarker: true })];
      const [firstParagraphEntry] = paragraphEntries;
      if (!firstParagraphEntry) {
        return {
          ...AlineTransforms.createListDefinition(editor, { listDefinition }),
          created: true,
        };
      }

      const [, firstParagraphPath] = firstParagraphEntry;
      const firstTextFormats = Queries.firstTextNodeFormats(editor, firstParagraphPath);
      const firstListItem = paragraphEntries.map(([paragraph]) => paragraph).find(isListItem);

      const listItemDetails = firstListItem
        ? Queries.getListItemDetails(editor, firstListItem)
        : null;
      if (
        listItemDetails &&
        Queries.listStylesEqual(listDefinition, listItemDetails.listDefinition)
      ) {
        return {
          created: false,
          listDefinition: listItemDetails.listDefinition,
          listDefinitionId: listItemDetails.activeListProps.listId,
        };
      }

      return {
        ...AlineTransforms.createListDefinition(editor, {
          listDefinition,
          markerFormats: firstTextFormats,
        }),
        created: true,
      };
    });
  },

  /**
   * Restart the list numbering from the beginning.
   */
  restartList(
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { at = editor.selection, authorId, editingMode } = options;
      if (!at) return;

      const startPoint = Editor.start(editor, at);
      const listItemEntries = Editor.nodes<ListItemElement>(editor, {
        at,
        match: isListItem,
      });
      const firstListItemEntry = listItemEntries.next().value;
      if (!firstListItemEntry) return;

      const [firstListItem] = firstListItemEntry;
      const listId = Queries.renderedParagraphListProps(firstListItem)?.listId;
      if (!listId) return;

      const nearestPreviousListEntry = Editor.previous<ListItemElement>(editor, {
        at: startPoint,
        match: node => node !== firstListItem && isListItem(node),
      });
      if (!nearestPreviousListEntry) return;

      const range = Editor.range(editor, startPoint, Editor.end(editor, []));
      const listItemsEntriesToChange = [
        ...Editor.nodes<ListItemElement>(editor, {
          at: range,
          match: node => {
            if (!isParagraphElement(node)) return false;

            const activeListProps = Queries.renderedParagraphListProps(node);
            return activeListProps?.listId === listId;
          },
        }),
      ];

      const listDefinition = Queries.getListDefinition(editor, listId);
      if (!listDefinition) return;

      const { listDefinitionId: nextListDefinitionId } = AlineTransforms.createListDefinition(
        editor,
        { listDefinition }
      );

      for (const [listItem, listItemPath] of listItemsEntriesToChange) {
        const activeListProps = Queries.renderedParagraphListProps(listItem);
        if (!activeListProps) return;

        AlineTransforms.setParagraphProps(
          editor,
          editingMode,
          authorId,
          {
            listProps: {
              listId: nextListDefinitionId,
              listLevel: activeListProps.listLevel,
            },
          },
          { at: listItemPath }
        );
      }
    });
  },

  /**
   * Selects the correct list marker if a list marker associated with a paragraph is clicked.
   * We mirror Word and select the marker for the list item that was clicked.
   */
  selectListMarker: (editor: Editor, clickedParaPath: Path): void => {
    AlineTransforms.withoutNormalizing(editor, () => {
      const paragraph = Node.get(editor, clickedParaPath);
      if (!isListItem(paragraph)) return;

      const point = Editor.start(editor, clickedParaPath);

      AlineTransforms.setSelection(editor, {
        eventSource: SelEventSource.MOUSE,
        props: {
          anchor: point,
          focus: point,
          listMarker: paragraph.listProps,
        },
      });
    });
  },

  /**
   * Sets props on a list definition.
   */
  setListDefinitionProps(
    editor: Editor,
    options: {
      editingMode: EditingMode;
      listDefinitionId: UUID;
      props: DeepPartial<ListDefinition>;
    }
  ): void {
    AlineTransforms.withoutNormalizing(editor, () => {
      const { listDefinitionId, props } = options;
      const {
        documentEntry: [, documentPath],
        listDefinitions,
      } = Queries.getNumbering(editor);
      const listDefinition = listDefinitions[listDefinitionId];
      if (!listDefinition) return;

      const mergedListDefinition: ListDefinition = merge({}, listDefinition, props);
      const newListDefinitions: ListDefinitions = {
        ...listDefinitions,
        [listDefinitionId]: mergedListDefinition,
      };

      Transforms.setNodes(editor, { listDefinitions: newListDefinitions }, { at: documentPath });
    });
  },

  /**
   * Toggles list items.  Returns the listId.
   */
  toggleListItems: (
    editor: Editor,
    options: {
      authorId: string;
      editingMode: EditingMode;
      at?: Location;
      listDefinition?: ListDefinition;
    }
  ): UUID | void => {
    const {
      at = editor.selection,
      authorId,
      editingMode,
      listDefinition = createListDefinition(),
    } = options;
    if (!at) return;

    const convertedPathRefs: PathRef[] = [];

    const { created, listDefinitionId, removed } = AlineTransforms.withoutNormalizing(
      editor,
      () => {
        const paragraphEntries = uniqBy(
          [...Queries.paragraphsAtLocation(editor, { at, listMarker: true })],
          ([, path]) => path.join(',')
        );
        const {
          created: createdListDefinition,
          listDefinition: foundListDefinition,
          listDefinitionId: toggledListId,
        } = AlineTransforms.findOrCreateToggleListDefinition(editor, { listDefinition });

        if (
          Queries.listStylesEqual(listDefinition, foundListDefinition) &&
          paragraphEntries.every(([paragraph]) => {
            const activeListProps = Queries.renderedParagraphListProps(paragraph);

            return activeListProps?.listId === toggledListId;
          })
        ) {
          for (const [, path] of paragraphEntries) {
            AlineTransforms.setParagraphProps(
              editor,
              editingMode,
              authorId,
              { indentationFirstLine: 0, indentationLeft: 0, listProps: null },
              { at: path }
            );
          }

          return { created: createdListDefinition, listDefinitionId: toggledListId, removed: true };
        }

        for (const [, path] of paragraphEntries)
          convertedPathRefs.push(Editor.pathRef(editor, path));

        // Convert everything in the selection to one contiguous list with the same listId.
        for (const [paragraph, path] of paragraphEntries) {
          const activeListProps = Queries.renderedParagraphListProps(paragraph);
          const props: Partial<ParagraphProps> = activeListProps
            ? { listProps: { ...activeListProps, listId: toggledListId } }
            : {
                ...createDefaultListItemIndentation(),
                listProps: { listId: toggledListId, listLevel: 0 },
              };

          AlineTransforms.setParagraphProps(editor, editingMode, authorId, props, { at: path });
        }

        return { created: createdListDefinition, listDefinitionId: toggledListId, removed: false };
      }
    );

    const convertedPaths = convertedPathRefs
      .map(pathRef => pathRef.unref())
      .filter(path => path !== null)
      .sort(Path.compare);
    if (removed || !created) return listDefinitionId;

    const firstParagraphPath = convertedPaths.at(0);
    if (!firstParagraphPath) return listDefinitionId;

    const currentBoundary = Queries.selectionBoundaryAboveLoc(editor, {
      at: firstParagraphPath,
    });
    if (!currentBoundary) return listDefinitionId;
    const [, currentBoundaryPath] = currentBoundary;

    const previousListItemEntry = Editor.previous<ListItemElement>(editor, {
      at: firstParagraphPath,
      match: (node, path) => {
        if (!isListItem(node)) return false;

        // Never continue a list across document / header / footer boundaries.
        const boundary = Queries.selectionBoundaryAboveLoc(editor, { at: path });
        if (!boundary || !Path.equals(boundary[1], currentBoundaryPath)) return false;

        const details = Queries.getListItemDetails(editor, node);
        // A paragraph this toggle just converted is not a list to continue from.
        if (!details || details.activeListProps.listId === listDefinitionId) return false;

        return Queries.listStylesEqual(listDefinition, details.listDefinition);
      },
    });
    if (!previousListItemEntry) return listDefinitionId;

    const previousListDetails = Queries.getListItemDetails(editor, previousListItemEntry[0]);
    if (!previousListDetails) return listDefinitionId;

    const continuedListId = previousListDetails.activeListProps.listId;
    const convertedEntries = convertedPaths.flatMap(path => [
      ...Queries.paragraphsAtLocation(editor, { at: path }),
    ]);

    HistoryTransforms.separateUndoCapture(editor, () => {
      AlineTransforms.withoutNormalizing(editor, () => {
        for (const [paragraph, path] of convertedEntries) {
          const activeListProps = Queries.renderedParagraphListProps(paragraph);
          if (activeListProps) {
            AlineTransforms.setParagraphProps(
              editor,
              editingMode,
              authorId,
              { listProps: { ...activeListProps, listId: continuedListId } },
              { at: path }
            );
          }
        }
      });
    });

    return continuedListId;
  },

  updateListLevel: (
    editor: Editor,
    options: {
      at: Path;
      authorId: string;
      direction: 'increase' | 'decrease';
      editingMode: EditingMode;
    }
  ): void => {
    const { at, authorId, direction, editingMode } = options;
    const props = Queries.consistentParagraphProps(editor, { at });
    if (!props?.listProps) return;

    const newListLevel = props.listProps.listLevel + (direction === 'increase' ? 1 : -1);

    if (isListLevel(newListLevel)) {
      AlineTransforms.setParagraphProps(
        editor,
        editingMode,
        authorId,
        {
          listProps: {
            ...props.listProps,
            listLevel: newListLevel,
          },
        },
        { at }
      );
    }
  },
  /**
   * Create a ListPaths object to keep track of each listId and its lastListPath.  This lets us know
   * how to build a list marker correctly.
   */
  upsertListPaths(
    listDefinitions: ListDefinitions,
    listPaths: ListPaths,
    activeListProps: NonNullable<ParagraphElement['listProps']>
  ): { listItemListPath: Path; newListPaths: ListPaths } {
    const { lastListPath } = listPaths[activeListProps.listId] ?? {};
    const listDefinition = listDefinitions[activeListProps.listId];

    const listItemListPath = Array.from({ length: activeListProps.listLevel + 1 }, (_, n) => {
      if (!lastListPath || typeof lastListPath[n] !== 'number') {
        if (!isListLevel(n)) return 0;
        // Use the start value from the list definition for this level, defaulting to 0 if not found
        return (listDefinition?.[n]?.start ?? 1) - 1;
      }

      return n === activeListProps.listLevel ? lastListPath[n] + 1 : lastListPath[n];
    });

    return {
      listItemListPath,
      newListPaths: {
        ...listPaths,
        [activeListProps.listId]: { lastListPath: listItemListPath },
      },
    };
  },
};
